import { motion } from 'framer-motion';
import { useData } from 'vike-react/useData';
import { ArrowRight, ShieldAlert } from 'lucide-react';
import { getAudienceById } from '@shared/constants/audiences';
import { Container } from '../../components/layout/Container';
import { CountUp } from '../../components/motion/CountUp';
import { Reveal } from '../../components/motion/Reveal';
import { StaggerGrid, StaggerItem } from '../../components/motion/StaggerGrid';
import { Button } from '../../components/ui/button';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { JourneyTimeline } from '../../components/blocks/JourneyTimeline';
import { ToolsForAudience } from '../../components/blocks/ToolsForAudience';
import { ConcreteCase } from '../../components/blocks/ConcreteCase';
import { SectorHero } from '../../components/sector/SectorHero';
import { SectorProof } from '../../components/sector/SectorProof';
import { RecommendedCeeFiches } from '../../components/sector/RecommendedCeeFiches';
import { SectorFAQ } from '../../components/sector/SectorFAQ';
import { SectorCTA } from '../../components/sector/SectorCTA';
import { getAudienceVoice } from '../../config/audience-voice';
import { audienceLegacyTone } from '../../config/audience-theme';
import { easeStripe } from '../../lib/motion';
import type { Data } from './+data';

const AUDIENCE_ID = 'industrie' as const;

/**
 * KPI cadre réglementaire & techniques industriels.
 * Chiffres factuels, non commerciaux (cf. AGENTS §8).
 */
const KPI = [
  {
    value: 'NF EN 16247 (audit énergétique réglementaire)',
    label: "Audit énergétique réglementaire",
    source: 'Décret n° 2014-1393 · directive 2012/27/UE',
  },
  {
    value: "ISO 50001 (management de l'énergie)",
    label: 'Système de management de l’énergie',
    source: "Dispense audit si périmètre ≥ 80 %",
  },
  {
    value: 'Fiches CEE industrielles IND-UT / IND-BA',
    label: 'Fiches CEE industrielles',
    source: 'Arrêtés primes énergie (CEE) en vigueur · secteur Industrie',
  },
];

export default function IndustriePage() {
  const { journey, tools, cases, ceeFiches } = useData<Data>();
  const audience = getAudienceById(AUDIENCE_ID)!;
  const voice = getAudienceVoice(AUDIENCE_ID);
  const legacyTone = audienceLegacyTone(AUDIENCE_ID);

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Industrie', url: 'https://solutionecofrance.fr/industrie' },
        ]}
      />

      <SectorHero
        audience={audience}
        voice={voice}
        sideSlot={<IndustrieDiagnosticPreview />}
      />

      {/* KPI bar — cadre réglementaire industriel */}
      <section className="border-y border-stone-200/60 bg-stone-50/50 py-10">
        <Container>
          <StaggerGrid className="grid gap-px overflow-hidden rounded-xl bg-stone-200/60 sm:grid-cols-3">
            {KPI.map((k) => (
              <StaggerItem key={k.label} className="bg-paper p-5">
                <p className="metric-xl">{k.value}</p>
                <p className="mt-1.5 text-xs text-stone-700">{k.label}</p>
                <p className="mt-0.5 text-[10px] text-stone-400">Source : {k.source}</p>
              </StaggerItem>
            ))}
          </StaggerGrid>
        </Container>
      </section>

      <SectorProof audience={audience} voice={voice} variant="bento" />

      {journey && (
        <JourneyTimeline
          eyebrow={voice.method.eyebrow}
          title={voice.method.title}
          description={voice.method.description}
          totalDuration={journey.totalDuration}
          ctaLabel={journey.ctaLabel}
          ctaHref={journey.ctaHref}
          steps={journey.steps}
          tone={legacyTone}
        />
      )}

      {tools.length > 0 && (
        <ToolsForAudience
          eyebrow={voice.tools.eyebrow}
          title={voice.tools.title}
          description={voice.tools.description}
          tools={tools.map((t) => ({
            title: t.title,
            description: t.description,
            iconName: t.iconName,
            href: t.href,
            badge: t.badge,
            metric: t.metric,
            metricLabel: t.metricLabel,
          }))}
          tone={legacyTone}
        />
      )}

      <RecommendedCeeFiches audience={audience} fiches={ceeFiches} />

      {cases.length > 0 && (
        <ConcreteCase
          eyebrow={voice.caseStudy.eyebrow}
          title={voice.caseStudy.title}
          cases={cases}
          tone={legacyTone}
        />
      )}

      {/* AUDIT RÉGLEMENTAIRE ALERT — spécifique aux grandes entreprises */}
      <section className="bg-stone-50/50 py-16 md:py-20">
        <Container className="max-w-5xl">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl border border-stone-300/60 bg-gradient-to-br from-stone-100 via-paper to-ivory-100 p-8 md:p-10">
              <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
                <div className="max-w-2xl">
                  <div className="inline-flex items-center gap-1.5 rounded-md bg-stone-200/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-700">
                    <ShieldAlert className="h-3 w-3" />
                    Obligation d'audit énergétique NF EN 16247
                  </div>
                  <h3 className="mt-4 h-section">
                    Votre audit énergétique réglementaire est-il à jour&nbsp;?
                  </h3>
                  <p className="mt-3 text-sm text-stone-600 leading-relaxed">
                    Toute entreprise répondant à au moins 2 des 3 critères (≥ 250 salariés,
                    ≥ 50 M€ CA, ≥ 43 M€ bilan) doit faire réaliser un audit énergétique selon
                    la norme NF EN 16247-1 tous les 4 ans. Contrôle par la DREAL territoriale,
                    sanctions administratives en cas de manquement (Code de l&rsquo;énergie,
                    article L. 233-7).
                  </p>
                  <Button asChild size="md" variant="primary" className="mt-6 group/btn">
                    <a href="/industrie/operations/audit-reglementaire">
                      Vérifier mon obligation d&rsquo;audit
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      <SectorFAQ audience={audience} voice={voice} />

      <SectorCTA audience={audience} voice={voice} />
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// IndustrieDiagnosticPreview — side card hero
// Aperçu d'un diagnostic CEE industriel (site agro-alimentaire),
// avec IPE par poste et leviers chiffrés indicatifs.
// Tone CORPORATE (stone) cohérent avec la palette industrie.
// ════════════════════════════════════════════════════════════════

function IndustrieDiagnosticPreview() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.18, ease: easeStripe }}
      className="rounded-2xl border border-stone-200/70 bg-paper p-5 shadow-[0_1px_2px_rgba(10,10,10,.04)] ring-1 ring-inset ring-stone-300/50 md:p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
            Diagnostic primes énergie (CEE) industriel
          </p>
          <p className="mt-0.5 text-xs text-stone-500">Site agro-alimentaire · 24 GWh/an</p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-md bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-700 ring-1 ring-inset ring-stone-200/60">
          <span className="h-1 w-1 rounded-full bg-stone-600 animate-glow-pulse" />
          Audité
        </span>
      </div>

      <div className="mt-5 flex items-baseline gap-2 font-display tracking-tight">
        <span className="text-3xl font-bold text-ink md:text-4xl">
          <CountUp end={540} suffix=" k€" />
        </span>
        <span className="text-base text-stone-500">Primes CEE estimées</span>
      </div>

      <div className="mt-5 space-y-1.5">
        {[
          {
            label: 'Récupération de chaleur perdue (fiche IND-UT-131)',
            val: '+ 218 k€',
            status: 'Coup de pouce',
            tone: 'accent',
          },
          {
            label: 'Variateurs de vitesse ventilation (fiche IND-UT-114)',
            val: '+ 142 k€',
            status: 'Éligible',
            tone: 'brand',
          },
          {
            label: "Optimisation de l'air comprimé (fiche IND-UT-117)",
            val: '+ 96 k€',
            status: 'À chiffrer',
            tone: 'lavender',
          },
          {
            label: 'Calorifugeage canalisations industrielles (fiche IND-UT-103)',
            val: '+ 84 k€',
            status: 'Éligible',
            tone: 'brand',
          },
        ].map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between rounded-lg border border-stone-200/70 bg-stone-50/40 px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs text-stone-700">{row.label}</p>
              <p
                className={`mt-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                  row.tone === 'brand'
                    ? 'text-brand-700'
                    : row.tone === 'lavender'
                      ? 'text-lavender-700'
                      : 'text-accent-700'
                }`}
              >
                {row.status}
              </p>
            </div>
            <span className="ml-2 font-mono text-xs font-semibold text-ink tabular-nums">
              {row.val}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-stone-200/60 pt-3">
        <p className="text-[11px] text-stone-500">Cumul Fonds chaleur ADEME</p>
        <p className="font-mono text-[12px] font-semibold text-stone-700 tabular-nums">
          + 180 k€
        </p>
      </div>
    </motion.div>
  );
}
